import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { Company } from 'src/app/models/data/company';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserWithRoleName } from 'src/app/models/data/user';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	DataSetTableComponent,
	DataSetRowClickDirective,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-company-user',
	templateUrl: './company-user.component.html',
	styleUrls: ['./company-user.component.scss', '../../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		DataSetLoadingRowComponent,
	],
})
export class CompanyUserComponent implements AfterViewInit, OnDestroy, OnInit {
	readonly companyId: string;

	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	@Input() company?: Company;

	displayedColumns: string[] = [
		'name',
		'email',
		'phoneNumber',
		'active',
		'roleName',
	];
	dataSource = new MatTableDataSource<UserWithRoleName>();

	canAddUser = false;
	companyIsSentrilock = false;

	fieldLabels = {
		defaultPlaceholder: $localize`:@@companyUser-defaultPlaceholder:Search name...`,
		name: $localize`:@@companyUser-filterLabelName:Name`,
		email: $localize`:@@companyUser-filterLabelEmail:Email`,
		phoneNumber: $localize`:@@companyUser-filterLabelPhoneNumber:Phone Number`,
		status: $localize`:@@companyUser-filterLabelStatus:Status`,
		roleName: $localize`:@@companyUser-filterLabelRoleName:Role`,
	};

	activeFilterOptions: DropdownOption[] = [
		{ value: '1', label: $localize`:@@companyUser-filterOptionActive:Active` },
		{
			value: '0',
			label: $localize`:@@companyUser-filterOptionInactive:Inactive`,
		},
	];

	private filterSubscription?: Subscription;

	getCompanyUsersByCompanyIdLoadingKey =
		ApiLoadingKeys.getCompanyUsersByCompanyId;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.companyId = this.route.snapshot.params.id;
	}

	async ngOnInit(): Promise<void> {
		await this.setCanAddUser();
	}

	ngAfterViewInit(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.getCompanyUsersByCompanyId(
						this.companyId,
						filter
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	async setCanAddUser(): Promise<void> {
		const hasUserRoleManagement = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);
		const hasGlobalCompanyMgmt = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
		const [sentrilockId, userProfile] = await Promise.all([
			this.apiService.getSentrilockCompanyId(),
			this.apiService.getUserProfile(),
		]);
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		let editingCompanyId = this.company?.id;
		if (!editingCompanyId) {
			editingCompanyId = activeCompanyId;
		}

		const isEditingActiveCompany = activeCompanyId === editingCompanyId;
		this.companyIsSentrilock = editingCompanyId === sentrilockId;

		this.canAddUser =
			(hasUserRoleManagement && isEditingActiveCompany) ||
			(hasGlobalCompanyMgmt && !this.companyIsSentrilock);
	}

	navigateToAddUser(): void {
		this.router.navigate(['/add-user'], {
			queryParams: { companyId: this.companyId },
		});
	}

	onRowClick(row: UserWithRoleName): void {
		this.router.navigate([
			`/company/${this.companyId}/detail/user/${row.user.id}/detail`,
		]);
	}

	formatSelectedUserName = (item: UserWithRoleName): string => {
		const firstName = item.user?.firstName || '';
		const lastName = item.user?.lastName || '';
		const lastNameInitial = lastName.charAt(0).toUpperCase();

		if (firstName && lastNameInitial) {
			return `${firstName} ${lastNameInitial}`;
		} else if (firstName) {
			return firstName;
		} else if (lastName) {
			return lastName;
		}

		return (
			item.user?.email ||
			$localize`:@@selectedDisplayFormat-unknownUser:Unknown User`
		);
	};

	rowId(row: UserWithRoleName): string {
		return row?.user?.id || 'unknown-user';
	}
}
